import { useQuery } from '@tanstack/react-query';
import { useProject } from '@/contexts/ProjectContext';
import { CATEGORY_DOT_COLORS } from '@/lib/pattern-categories';
import { getGBrainPatterns, getGBrainContext } from '@/lib/api';

/**
 * PatternSummaryPanel
 *
 * Displays count of patterns by category with colored dots
 * Fetches from getGBrainPatterns and getGBrainContext
 */
export function PatternSummaryPanel(): React.ReactElement | null {
  const { selectedProjectId } = useProject();

  const { data: patternsData } = useQuery({
    queryKey: ['g-brain-patterns', selectedProjectId],
    queryFn: () => {
      if (!selectedProjectId) throw new Error('No project selected');
      return getGBrainPatterns(selectedProjectId);
    },
    enabled: !!selectedProjectId,
    staleTime: 10_000,
    refetchInterval: 10_000,
  });

  const { data: contextData } = useQuery({
    queryKey: ['g-brain-context', selectedProjectId],
    queryFn: () => {
      if (!selectedProjectId) throw new Error('No project selected');
      return getGBrainContext(selectedProjectId);
    },
    enabled: !!selectedProjectId,
    staleTime: 30_000,
  });

  const patterns = patternsData?.patterns ?? [];
  const context = contextData?.context;

  // Pattern category counts
  const categoryCounts = patterns.reduce<Record<string, number>>((acc, p) => {
    acc[p.category] = (acc[p.category] ?? 0) + 1;
    return acc;
  }, {});

  if (!selectedProjectId) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
        Pattern Summary
      </div>

      {patterns.length > 0 ? (
        <div className="space-y-1.5">
          <div className="text-2xl font-bold text-text-primary">
            {patterns.length}
            <span className="text-xs font-normal text-text-secondary ml-1">patterns</span>
          </div>

          <div className="space-y-1">
            {Object.entries(categoryCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([category, count]) => (
                <div key={category} className="flex items-center gap-2">
                  <div
                    className={`h-2 w-2 flex-shrink-0 rounded-full ${CATEGORY_DOT_COLORS[category] ?? 'bg-border'}`}
                  />
                  <span className="flex-1 truncate text-xs text-text-secondary">
                    {category.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs font-semibold text-text-primary">{count}</span>
                </div>
              ))}
          </div>

          {context?.lastScannedAt && (
            <div className="text-[10px] text-text-tertiary pt-1 border-t border-border/50">
              Scanned:{' '}
              {new Date(context.lastScannedAt).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded bg-surface-elevated/50 p-2 text-center text-[10px] text-text-tertiary">
          No patterns yet — run a discovery workflow
        </div>
      )}
    </div>
  );
}
